import { utils } from 'apprise-core/utils'
import { PropsWithChildren, useMemo } from 'react'
import { useCall } from './call'
import { PreloaderProvider, usePreloaders } from './context'


// <Preload> holds async tasks for children to join on render, potentially reducing the render-blocking wait.
// usePreload() retrieves a task by name so it can be awaited. If no such tasks exists the child should start one.

// note: tasks are deleted once they're resolved, so that preloading doesn't come in the way of data refresh policies.

// tasks may be passed to <Preload> as props, and in this case <Preload> starts them in parallel.
// however, maximum gain comes from starting tasks in an inline script in `index.html`, before React loads.
// <Preload> will look for these "external tasks" in the `document.apprise_preload` global.

// note: the tradeoff of preloading, particularly ahead of render, is the loss of task encapsulation in dedicated components, particularly across library boundaries.
// consider it as a low-level optimisation to pursue in production.


export type Preloader = {

    name: string
    task: (call: ReturnType<typeof useCall>) => Promise<any>

}

export type PreloaderProps = PropsWithChildren<Partial<{

    loaders: Preloader[]
}>>


export const Preload = (props: PreloaderProps) => {

    const { children, loaders = [] } = props

    const call = useCall()

     const externalLoaders = (document as any).apprise_preload ?? {}

    const tasks = useMemo(() => {
       
        const theseloaders = loaders.filter(l => !externalLoaders[l.name])

        return { ...externalLoaders, ...utils().index(theseloaders).mappingBy(l => l.name, l => l.task(call)) }

        // eslint-disable-next-line
    }, [externalLoaders])

    return <PreloaderProvider loaders={tasks}>
        {children}
    </PreloaderProvider>


}


export const usePreload = () => {

    const state = usePreloaders()


    const self = {


        get: <T extends object>(name: string) =>


            state.get().loaders?.[name]?.finally(() => {

                state.set(s => delete s.loaders[name])

            }) as Promise<T>


    }

    return self
}